import { useIdVerificationWebApiHandler_performIdentityCheck } from '@zg-rentals/ts-id-verification-client-react-query';
import {
  useId,
  useIdVerificationCallbacks,
  usePidAnswers,
  useReauthenticating,
  useSetKiqQuestionSet,
  useTermSet,
} from '../IdvContext';
import { useCallback, useContext } from 'react';
import { LANDLORD_CLICKSTREAM_EVENTS, RENTER_CLICKSTREAM_EVENTS } from '../clickstream/events';
import { ExitReason, LogLevel, TermSet } from '../../../models';
import { ErrorWizardStepId, WizardStepId } from '../wizard/wizardTypes';
import { DatarouterError } from '@zg-rentals/web-base';
import { ErrorCodes } from '../constants/errorCodes';
import { getZgAuthZoneFromTermSet } from '../utils/zgAuthZoneUtils';
import usePassiveAuth from './usePassiveAuth';
import { WizardContext } from '../wizard/WizardContext';
import { LANDLORD_METRIC_PREFIX, RENTER_METRIC_PREFIX } from '../Idv';

export default function usePerformIdentityCheck() {
  const pidAnswers = usePidAnswers();
  const termSet = useTermSet();
  const requestingClient = useId();
  const reauthenticating = useReauthenticating();
  const { data: passiveAuthData } = usePassiveAuth();
  const hdimPayload = passiveAuthData?.hdimPayload ?? '';
  const jsc = passiveAuthData?.jsc ?? '';
  const { onSubmitPidForm, trackEvent, emitMetric, log, onExit } = useIdVerificationCallbacks();
  const setKiqQuestionSet = useSetKiqQuestionSet();
  const { goToStep, goToErrorStep, currentStep } = useContext(WizardContext);

  const { mutate } = useIdVerificationWebApiHandler_performIdentityCheck({
    onMutate() {
      const hasPassiveAuthData = hdimPayload && jsc;
      if (!hasPassiveAuthData) {
        log(LogLevel.Warn, {
          message: 'Missing data to passively authenticate',
        });
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} missingJscOrHdim`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} missingJscOrHdim`);
        }
      }
      if (termSet === TermSet.LandlordScreening) {
        trackEvent(LANDLORD_CLICKSTREAM_EVENTS.clickNextSubmitIDV);
      } else if (pidAnswers.ssn) {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitIdvWithSsn);
      } else {
        trackEvent(RENTER_CLICKSTREAM_EVENTS.submitIdv);
      }
      onSubmitPidForm({
        ...pidAnswers,
        // Don't include sensitive information
        ssn: '',
        dob: '',
      });
    },
    onSuccess: async (data) => {
      if (data.passiveAuthSucceeded) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} passiveAuth success`);
          trackEvent(LANDLORD_CLICKSTREAM_EVENTS.idvSuccess);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} passiveAuth success`);
        }
        await onExit({ reason: ExitReason.IdVerified, success: true });
      } else if (data.kiqEnabled && data.questionSet && data.questionSet.length > 0) {
        if (termSet === TermSet.LandlordScreening) {
          emitMetric(`${LANDLORD_METRIC_PREFIX} kbaStepUp`);
        } else {
          emitMetric(`${RENTER_METRIC_PREFIX} kbaStepUp`);
        }
        setKiqQuestionSet(data.questionSet);
        if (currentStep.id === WizardStepId.KiqOnboarding) {
          goToStep(WizardStepId.KiqQuestions);
        } else {
          goToStep(WizardStepId.KiqOnboarding);
        }
      } else {
        log(LogLevel.Error, {
          message: 'Error calling performPassiveIdentityCheck - no question set',
        });
        goToErrorStep(ErrorWizardStepId.Generic);
      }
    },
    onError(error) {
      if (termSet === TermSet.LandlordScreening) {
        emitMetric(`${LANDLORD_METRIC_PREFIX} performIdentityCheck error`);
      } else {
        emitMetric(`${RENTER_METRIC_PREFIX} performIdentityCheck error`);
      }
      if (DatarouterError.is(error)) {
        if (error.responseStatus === 401) {
          onExit({ reason: ExitReason.NotAuthenticated, success: false });
        } else {
          switch (error.code) {
            case ErrorCodes.USER_NOT_FOUND:
              goToErrorStep(ErrorWizardStepId.UserNotFound);
              break;
            case ErrorCodes.USER_NOT_FOUND_LOCKED_OUT:
              goToErrorStep(ErrorWizardStepId.UserNotFoundLockedOut);
              break;
            case ErrorCodes.USER_LOCKED_OUT:
              goToErrorStep(ErrorWizardStepId.UserLockedOut);
              break;
            case ErrorCodes.FROZEN_CREDIT_REPORT:
              goToErrorStep(ErrorWizardStepId.FrozenAccount);
              break;
            default:
              log(LogLevel.Error, {
                message: `Unexpected error code for performIdentityCheck: ${error.code}`,
                error,
              });
              goToErrorStep(ErrorWizardStepId.Generic);
              break;
          }
        }
      } else {
        log(LogLevel.Error, {
          message: 'Unexpected error response for performIdentityCheck',
          error,
        });
        goToErrorStep(ErrorWizardStepId.Generic);
      }
    },
  });

  return useCallback(
    () =>
      mutate({
        postData: {
          ...pidAnswers,
          deviceType: 'desktop',
          requestingClient,
          zgAuthZone: getZgAuthZoneFromTermSet(termSet),
          isReAuthRequest: reauthenticating,
          hdimPayload,
          jsc,
        },
      }),
    [mutate, pidAnswers, requestingClient, termSet, reauthenticating, hdimPayload, jsc],
  );
}
